/**
 * useSiteMapViewState: the view state of this visit to the site map.
 *
 * Keyed by the router's history entry key, which the browser keeps for each entry across
 * back, forward and reload. Going back to the site map therefore restores what that visit
 * was showing, while arriving through a link or the menu is a new entry and starts fresh.
 */
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { SiteMapViewState, SiteMapViewStore } from '../models/siteMapModels';
import { sessionViewStore } from '../storage/sessionViewStore';
import { EMPTY_VIEW_STATE } from '../utils/viewStateHistory';

export interface SiteMapViewStateResult {
  /** The state to start from, read once when the page mounts */
  readonly initial: SiteMapViewState;
  readonly update: (patch: Partial<SiteMapViewState>) => void;
}

export function useSiteMapViewState(store: SiteMapViewStore = sessionViewStore): SiteMapViewStateResult {
  const { key } = useLocation();
  const [initial] = useState(() => store.load(key) ?? EMPTY_VIEW_STATE);
  const update = useCallback((patch: Partial<SiteMapViewState>) => store.update(key, patch), [store, key]);
  return { initial, update };
}
